import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend);

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const parseAmount = (val) => {
  if (!val) return 0;
  return parseFloat(val.toString().replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;
};

const getWeekNumber = (dateStr) => {
  const date = new Date(dateStr);
  const firstJan = new Date(date.getFullYear(), 0, 1);
  const pastDays = (date - firstJan) / 86400000;
  return Math.ceil((pastDays + firstJan.getDay() + 1) / 7);
};

const WeekPurchaseComparisonChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    const years = new Set();
    data.forEach((row) => {
      const date = new Date(row.DataZakupu);
      if (!isNaN(date)) years.add(date.getFullYear());
    });

    const sortedYears = Array.from(years).sort((a, b) => a - b);
    setAvailableYears(sortedYears);
    if (sortedYears.length > 0) setSelectedYear(sortedYears[sortedYears.length - 1]);
  }, [data]);

  useEffect(() => {
    if (!selectedYear) return;

    const current = {};
    const previous = {};
    data.forEach((row) => {
      const date = new Date(row.DataZakupu);
      if (isNaN(date)) return;

      const year = date.getFullYear();
      const week = getWeekNumber(row.DataZakupu);
      const value = parseAmount(row.K_42);

      if (year === selectedYear) {
        current[week] = (current[week] || 0) + value;
      } else if (year === selectedYear - 1) {
        previous[week] = (previous[week] || 0) + value;
      }
    });

    const weeks = Array.from({ length: 52 }, (_, i) => i + 1);
    const currentValues = weeks.map((w) => current[w] || 0);
    const previousValues = weeks.map((w) => previous[w] || 0);

    const max = Math.max(...currentValues);
    const min = Math.min(...currentValues.filter((v) => v > 0));

    const barColors = currentValues.map((v) => {
      if (v === max) return "#9848c7";
      if (v === min) return "rgba(152, 72, 199, 0.3)";
      return "rgba(152, 72, 199, 0.5)";
    });

    setChartData({
      labels: weeks.map((w) => `Tydzień ${w}`),
      datasets: [
        {
          label: `Zakupy (${selectedYear})`,
          data: currentValues,
          backgroundColor: barColors,
          borderColor: "#9848c7",
          type: "bar",
        },
        {
          label: `Trend Zakupów (${selectedYear})`,
          data: currentValues,
          type: "line",
          borderColor: "#9848c7",
          backgroundColor: "rgba(152, 72, 199, 0.3)",
          fill: false,
          tension: 0.4,
        },
        {
          label: `Trend Zakupów (${selectedYear - 1})`,
          data: previousValues,
          type: "line",
          borderColor: "#aaa",
          backgroundColor: "rgba(180, 180, 180, 0.3)",
          fill: false,
          tension: 0.4,
        },
      ],
    });
  }, [data, selectedYear]);

  const handleYearChange = (e) => setSelectedYear(parseInt(e.target.value, 10));

  if (!availableYears.length) return <p>Ładowanie filtrów...</p>;
  if (!chartData) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="week-purchase-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="week-purchase-tooltip"
        place="left"
        content={
          <>
            <strong>Zakupy tygodniowe (52 tygodnie)</strong>
            <br />
            Porównanie wartości zakupów w tygodniach <br />
            dla lat {selectedYear} i {selectedYear - 1}.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear}
            onChange={handleYearChange}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Zakupy (PLN)",
              },
              ticks: {
                callback: (value) =>
                  Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " "),
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: `Zakupy tygodniowe w roku – ${selectedYear}`,
            },
            legend: {
              display: true,
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const value = context.parsed.y || 0;
                  return `${context.dataset.label}: ${value
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}`;
                },
              },
            },
          },
        }}
      />
    </ChartWrapper>
  );
};

export default WeekPurchaseComparisonChart;
